import { useState } from "react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EVENT_FULL, STALE_VERSION } from "@shared/registrationApproval";
import { WAITLIST_REGISTRANT_COPY } from "@shared/registrationWaitlist";

/** `events.decline` response (PR #21). */
export type DeclineRegistrationResult = {
  ok: boolean;
  alreadyApplied: boolean;
  registrationId: number;
  status: "waitlisted";
};

export type DeclineProcedureError = {
  message?: string;
  data?: { code?: string; reason?: string } | null;
  cause?: { reason?: string } | null;
};

/**
 * Admin-facing, but held to the registrant copy rules. Capacity copy is shown
 * only for PRECONDITION_FAILED + EVENT_FULL.
 */
export function declineErrorNotice(error: DeclineProcedureError): { title: string; description: string } {
  const reason = error.data?.reason ?? error.cause?.reason;
  if (reason === STALE_VERSION || error.data?.code === "CONFLICT") {
    return {
      title: "This registration was just updated",
      description: "The list has been refreshed. Review it and choose again.",
    };
  }
  if (error.data?.code === "PRECONDITION_FAILED" && reason === EVENT_FULL) {
    return {
      title: "This event is at capacity",
      description: "Nothing was changed. The list has been refreshed.",
    };
  }
  return {
    title: "Decline didn't go through",
    description: "Nothing was changed. Please try again in a moment.",
  };
}

/**
 * Admin Decline for a registration under review.
 *
 * The only thing it does is call `events.decline`; that procedure owns the
 * transition to `waitlisted`. Nothing here decides from seat counts — the admin
 * chooses, and confirms, every Decline.
 */
export default function DeclineRegistrationButton({ registrationId, expectedVersion, attendeeName, eventSlug }: {
  registrationId: number;
  /** The row's `reviewVersion`, sent so a decline on a stale row is refused. */
  expectedVersion: number;
  attendeeName: string;
  eventSlug: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const utils = trpc.useUtils();
  const refreshAfterDecline = () => {
    void utils.events.attendeeRoster.invalidate({ eventSlug });
    void utils.events.listPending.invalidate();
    void utils.events.adminRegistrationCounts.invalidate();
  };

  const decline = trpc.events.decline.useMutation({
    onSuccess: (result: DeclineRegistrationResult) => {
      void refreshAfterDecline();
      if (!result.ok) {
        chiNotify.info("Decline didn't go through", { description: "Nothing was changed. The list has been refreshed." });
        return;
      }
      if (result.alreadyApplied) {
        chiNotify.info(`${attendeeName} is already on the waitlist`, { description: "Nothing else was changed." });
        return;
      }
      chiNotify.success(`${attendeeName} is on the waitlist`, {
        description: "Registration declined and moved to the waitlist.",
      });
    },
    onError: error => {
      void refreshAfterDecline();
      const notice = declineErrorNotice(error);
      chiNotify.info(notice.title, { description: notice.description });
    },
    onSettled: () => setConfirming(false),
  });

  return (
    <>
      <button
        type="button"
        title={`Decline ${attendeeName} and add them to the waitlist`}
        disabled={decline.isPending}
        onClick={() => setConfirming(true)}
        style={{ height: 28, padding: "0 0.6rem", borderRadius: "0.375rem", background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.25)", color: "#F5A623", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, cursor: decline.isPending ? "wait" : "pointer" }}
      >
        {decline.isPending ? "Declining…" : "Decline"}
      </button>
      <AlertDialog open={confirming} onOpenChange={open => { if (!decline.isPending) setConfirming(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline {attendeeName}?</AlertDialogTitle>
            <AlertDialogDescription>
              They move to the waitlist and are told: “{WAITLIST_REGISTRANT_COPY}”
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={decline.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={decline.isPending}
              onClick={event => {
                event.preventDefault();
                decline.mutate({ registrationId, expectedVersion });
              }}
            >
              {decline.isPending ? "Declining…" : "Decline"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
